import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Role } from '../../../core/enum/role.enum';
import { Item } from '../../../core/models/share/item';
import { UserProfile } from '../../../core/models/user/user-profile';
import { SettingService } from '../../../core/services/api/setting.service';
import { ErrorService } from '../../../core/services/error.service';

// INC-289 (UAT): EmploymentSector is server-mandatory for individual investors
// (UpdateIndividualInvestorProfile rejects with ERR_REQ_EMP_SECTOR when it's
// absent), so an investor missing it can't save ANY section from the
// review-attest screen. This sub-component makes JUST that one field editable
// there — scoped deliberately to employmentSector (occupation/income/source-of-
// funds/PEP are NOT server-required, so they stay only on the full profile
// page). The validator + dropdown source are reused verbatim from
// profile-details.component.ts (buildForm() / getProfileDropdown()).
@Component({
  standalone: false,
  selector: 'app-employment-section-form',
  templateUrl: './employment-section-form.component.html',
  styleUrls: ['./employment-section-form.component.scss'],
})
export class EmploymentSectionFormComponent implements OnInit, OnChanges {
  @Input() profile: UserProfile;
  @Input() role: Role;
  @Input() showFormError = false;

  form: UntypedFormGroup;
  employmentSectors: Item[];
  occupationType: Item[];

  readonly othersId = 255;

  Role: typeof Role = Role;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private settingService: SettingService,
    private errorService: ErrorService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.getEmploymentSectors();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Guarded on `this.form` since the very first change (the one Angular
    // fires before ngOnInit) arrives before buildForm() has run; ngOnInit
    // does that initial seed instead.
    if (changes.profile && this.profile && this.form) {
      this.seedFromProfile();
    }
  }

  buildForm(): void {
    this.form = this.formBuilder.group({
      // occupationType (employment status) is required CLIENT-SIDE for individual
      // investors (INC-283), with an "Other → specify" free-text follow-up
      // (othersOccupationType). Validators mirror profile-details.component.ts. NOTE:
      // the backend does NOT enforce it (it's not in the SC-mandatory set / the
      // MissingMandatoryField predicate) — same as the standalone profile-details page;
      // this validator is the only gate, so a direct API call could omit it (INC-324).
      occupationType: [null, this.role === Role.IndividualInvestor ? Validators.required : null],
      othersOccupationType: [null, Validators.maxLength(512)],
      employmentSector: [
        null,
        this.role === Role.IndividualInvestor ? Validators.required : null,
      ],
    });

    if (this.profile) {
      this.seedFromProfile();
    }
  }

  // Seeds every control from the profile, then re-applies the "Other" follow-up
  // validity so a pre-existing occupationType === othersId keeps its required
  // othersOccupationType (a plain patchValue does not run changeValidity).
  private seedFromProfile(): void {
    this.form.patchValue(this.profile);
    this.changeValidity(this.form.value.occupationType == this.othersId, 'othersOccupationType');
  }

  getEmploymentSectors(): void {
    const observable =
      this.role == Role.IndividualInvestor
        ? this.settingService.getProfileDropdown()
        : this.settingService.getCorporateRepDropdown();

    observable.subscribe({
      next: (data) => {
        this.employmentSectors = data.employmentSector;
        this.occupationType = data.occupationType;
      },
      error: (err) => this.errorService.showError(err),
    });
  }

  // Makes othersOccupationType required when "Other" (othersId) is picked and
  // clears it otherwise. Copied from profile-details.component.ts.
  changeValidity(isOther: boolean, otherControl: string): void {
    if (isOther) {
      this.form.controls[otherControl].setValidators([Validators.required]);
    } else {
      this.form.controls[otherControl].setValue(null);
      this.form.controls[otherControl].clearValidators();
    }

    this.form.controls[otherControl].updateValueAndValidity();
  }
}
